import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

const TargetCursor = ({ targetSelector = '.cursor-target', spinDuration = 2, hideDefaultCursor = true, color = 'blue' }) => {
  const location = useLocation();
  const cursorRef = useRef(null);
  const cornersRef = useRef(null);
  const spinTl = useRef(null);
  const dotRef = useRef(null);
  const constants = useMemo(
    () => ({
      borderWidth: 2,        // Thinner borders for subtle look
      cornerSize: 8,         // Smaller brackets (was 12)
      parallaxStrength: 0.00005
    }),
    []
  );

  const moveCursor = useCallback((x, y) => {
    if (!cursorRef.current) return;
    gsap.to(cursorRef.current, {
      x,
      y,
      duration: 0.1,
      ease: 'power3.out'
    });
  }, []);

  useEffect(() => {
    if (!cursorRef.current) return;

    const originalCursor = document.body.style.cursor;
    
    // Function to update cursor visibility based on screen size
    const updateCursorVisibility = () => {
      if (hideDefaultCursor && window.innerWidth >= 1024) {
        document.body.style.cursor = 'none';
      } else {
        document.body.style.cursor = originalCursor || 'auto';
      }
    };
    
    // Set initial cursor state
    updateCursorVisibility();
    
    // Listen for window resize to update cursor visibility
    const handleResize = () => {
      updateCursorVisibility();
    };
    window.addEventListener('resize', handleResize);

    const cursor = cursorRef.current;
    cornersRef.current = cursor.querySelectorAll('.target-cursor-corner');

    let activeTarget = null;
    let currentTargetMove = null;
    let currentLeaveHandler = null;
    let isAnimatingToTarget = false;
    let resumeTimeout = null;

    const cleanupTarget = target => {
      if (currentTargetMove) {
        target.removeEventListener('mousemove', currentTargetMove);
      }
      if (currentLeaveHandler) {
        target.removeEventListener('mouseleave', currentLeaveHandler);
      }
      currentTargetMove = null;
      currentLeaveHandler = null;
    };

    gsap.set(cursor, {
      xPercent: -50,
      yPercent: -50,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    });

    // Initialize corner bracket positions - very close to center for "expanding dot" effect
    // ⚙️ ADJUST DISTANCE: Change the multiplier (0.3 = very close, 1.0 = medium, 1.5 = far)
    // Make sure to change BOTH here AND in the leaveHandler below (around line ~269)
    const { cornerSize } = constants;
    const initialPositions = [
      { x: -cornerSize * 0.3, y: -cornerSize * 0.3 },  // top-left
      { x: cornerSize * 0.3, y: -cornerSize * 0.3 },   // top-right
      { x: cornerSize * 0.3, y: cornerSize * 0.3 },    // bottom-right
      { x: -cornerSize * 0.3, y: cornerSize * 0.3 }    // bottom-left
    ];
    
    Array.from(cornersRef.current).forEach((corner, index) => {
      gsap.set(corner, {
        x: initialPositions[index].x,
        y: initialPositions[index].y
      });
    });

    const createSpinTimeline = () => {
      if (spinTl.current) {
        spinTl.current.kill();
      }
      spinTl.current = gsap
        .timeline({ repeat: -1 })
        .to(cursor, { rotation: '+=360', duration: spinDuration, ease: 'none' });
    };

    createSpinTimeline();

    const moveHandler = e => moveCursor(e.clientX, e.clientY);
    window.addEventListener('mousemove', moveHandler);

    const scrollHandler = () => {
      if (!activeTarget || !cursorRef.current) return;

      const mouseX = gsap.getProperty(cursorRef.current, 'x');
      const mouseY = gsap.getProperty(cursorRef.current, 'y');

      const elementUnderMouse = document.elementFromPoint(mouseX, mouseY);
      const isStillOverTarget =
        elementUnderMouse &&
        (elementUnderMouse === activeTarget || elementUnderMouse.closest(targetSelector) === activeTarget);

      if (!isStillOverTarget) {
        if (currentLeaveHandler) {
          currentLeaveHandler();
        }
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    window.addEventListener('mousemove', moveHandler);
    const mouseDownHandler = () => {
      if (!dotRef.current) return;
      gsap.to(dotRef.current, { scale: 0.7, duration: 0.3 });
      gsap.to(cursorRef.current, { scale: 0.9, duration: 0.2 });
    };

    const mouseUpHandler = () => {
      if (!dotRef.current) return;
      gsap.to(dotRef.current, { scale: 1, duration: 0.3 });
      gsap.to(cursorRef.current, { scale: 1, duration: 0.2 });
    };

    window.addEventListener('mousedown', mouseDownHandler);
    window.addEventListener('mouseup', mouseUpHandler);

    const enterHandler = e => {
      const directTarget = e.target;

      const allTargets = [];
      let current = directTarget;
      while (current && current !== document.body) {
        if (current.matches(targetSelector)) {
          allTargets.push(current);
        }
        current = current.parentElement;
      }

      const target = allTargets[0] || null;
      if (!target || !cursorRef.current || !cornersRef.current) return;

      if (activeTarget === target) return;

      if (activeTarget) {
        cleanupTarget(activeTarget);
      }

      if (resumeTimeout) {
        clearTimeout(resumeTimeout);
        resumeTimeout = null;
      }

      activeTarget = target;
      const corners = Array.from(cornersRef.current);
      corners.forEach(corner => {
        gsap.killTweensOf(corner);
      });
      gsap.killTweensOf(cursorRef.current, 'rotation');
      spinTl.current?.pause();

      gsap.set(cursorRef.current, { rotation: 0 });

      const updateCorners = (mouseX, mouseY) => {
        const rect = target.getBoundingClientRect();
        const cursorRect = cursorRef.current.getBoundingClientRect();

        const cursorCenterX = cursorRect.left + cursorRect.width / 2;
        const cursorCenterY = cursorRect.top + cursorRect.height / 2;

        const [tlc, trc, brc, blc] = Array.from(cornersRef.current);

        const { borderWidth, cornerSize, parallaxStrength } = constants;

        let tlOffset = {
          x: rect.left - cursorCenterX - borderWidth,
          y: rect.top - cursorCenterY - borderWidth
        };
        let trOffset = {
          x: rect.right - cursorCenterX + borderWidth - cornerSize,
          y: rect.top - cursorCenterY - borderWidth
        };
        let brOffset = {
          x: rect.right - cursorCenterX + borderWidth - cornerSize,
          y: rect.bottom - cursorCenterY + borderWidth - cornerSize
        };
        let blOffset = {
          x: rect.left - cursorCenterX - borderWidth,
          y: rect.bottom - cursorCenterY + borderWidth - cornerSize
        };

        if (mouseX !== undefined && mouseY !== undefined) {
          const targetCenterX = rect.left + rect.width / 2;
          const targetCenterY = rect.top + rect.height / 2;
          const mouseOffsetX = (mouseX - targetCenterX) * parallaxStrength;
          const mouseOffsetY = (mouseY - targetCenterY) * parallaxStrength;

          tlOffset.x += mouseOffsetX;
          tlOffset.y += mouseOffsetY;
          trOffset.x += mouseOffsetX;
          trOffset.y += mouseOffsetY;
          brOffset.x += mouseOffsetX;
          brOffset.y += mouseOffsetY;
          blOffset.x += mouseOffsetX;
          blOffset.y += mouseOffsetY;
        }

        const tl = gsap.timeline();
        const corners = [tlc, trc, brc, blc];
        const offsets = [tlOffset, trOffset, brOffset, blOffset];

        corners.forEach((corner, index) => {
          tl.to(
            corner,
            {
              x: offsets[index].x,
              y: offsets[index].y,
              duration: 0.2,
              ease: 'power2.out'
            },
            0
          );
        });
      };

      isAnimatingToTarget = true;
      updateCorners();

      setTimeout(() => {
        isAnimatingToTarget = false;
      }, 1);

      let moveThrottle = null;
      const targetMove = ev => {
        if (moveThrottle || isAnimatingToTarget) return;
        moveThrottle = requestAnimationFrame(() => {
          const mouseEvent = ev;
          updateCorners(mouseEvent.clientX, mouseEvent.clientY);
          moveThrottle = null;
        });
      };

      const leaveHandler = () => {
        activeTarget = null;
        isAnimatingToTarget = false;

        if (cornersRef.current) {
          const corners = Array.from(cornersRef.current);
          gsap.killTweensOf(corners);

          const { cornerSize } = constants;
          // ⚙️ ADJUST BRACKET DISTANCE: Change multiplier (0.3 = very close, 1.5 = far)
          const positions = [
            { x: -cornerSize * 0.3, y: -cornerSize * 0.3 },  // top-left
            { x: cornerSize * 0.3, y: -cornerSize * 0.3 },   // top-right
            { x: cornerSize * 0.3, y: cornerSize * 0.3 },    // bottom-right
            { x: -cornerSize * 0.3, y: cornerSize * 0.3 }    // bottom-left
          ];

          const tl = gsap.timeline();
          corners.forEach((corner, index) => {
            tl.to(
              corner,
              {
                x: positions[index].x,
                y: positions[index].y,
                duration: 0.3,
                ease: 'power3.out'
              },
              0
            );
          });
        }

        resumeTimeout = setTimeout(() => {
          if (!activeTarget && cursorRef.current && spinTl.current) {
            const currentRotation = gsap.getProperty(cursorRef.current, 'rotation');
            const normalizedRotation = currentRotation % 360;

            spinTl.current.kill();
            spinTl.current = gsap
              .timeline({ repeat: -1 })
              .to(cursorRef.current, { rotation: '+=360', duration: spinDuration, ease: 'none' });

            gsap.to(cursorRef.current, {
              rotation: normalizedRotation + 360,
              duration: spinDuration * (1 - normalizedRotation / 360),
              ease: 'none',
              onComplete: () => {
                spinTl.current?.restart();
              }
            });
          }
          resumeTimeout = null;
        }, 50);

        cleanupTarget(target);
      };

      currentTargetMove = targetMove;
      currentLeaveHandler = leaveHandler;

      target.addEventListener('mousemove', targetMove);
      target.addEventListener('mouseleave', leaveHandler);
    };

    window.addEventListener('mouseover', enterHandler, { passive: true });

    return () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseover', enterHandler);
      window.removeEventListener('scroll', scrollHandler);

      if (activeTarget) {
        cleanupTarget(activeTarget);
      }

      spinTl.current?.kill();
      window.removeEventListener('resize', handleResize);
      document.body.style.cursor = originalCursor || 'auto';
    };
  }, [targetSelector, spinDuration, moveCursor, constants, hideDefaultCursor]);

  useEffect(() => {
    if (!cursorRef.current || !spinTl.current) return;

    if (spinTl.current.isActive()) {
      spinTl.current.kill();
      spinTl.current = gsap
        .timeline({ repeat: -1 })
        .to(cursorRef.current, { rotation: '+=360', duration: spinDuration, ease: 'none' });
    }
  }, [spinDuration]);

  // Reset cursor state when route changes
  useEffect(() => {
    if (!cursorRef.current || !cornersRef.current) return;

    const corners = Array.from(cornersRef.current);
    const { cornerSize } = constants;

    // Reset corners to default collapsed position
    const positions = [
      { x: -cornerSize * 0.3, y: -cornerSize * 0.3 },  // top-left
      { x: cornerSize * 0.3, y: -cornerSize * 0.3 },   // top-right
      { x: cornerSize * 0.3, y: cornerSize * 0.3 },    // bottom-right
      { x: -cornerSize * 0.3, y: cornerSize * 0.3 }    // bottom-left
    ];

    corners.forEach((corner, index) => {
      gsap.killTweensOf(corner);
      gsap.to(corner, {
        x: positions[index].x,
        y: positions[index].y,
        duration: 0.2,
        ease: 'power3.out'
      });
    });

    // Reset rotation and restart spin
    gsap.set(cursorRef.current, { rotation: 0 });
    if (spinTl.current) {
      spinTl.current.kill();
      spinTl.current = gsap
        .timeline({ repeat: -1 })
        .to(cursorRef.current, { rotation: '+=360', duration: spinDuration, ease: 'none' });
    }
  }, [location.pathname, constants, spinDuration]);

  return (
    <div
      ref={cursorRef}
      className="hidden lg:block fixed top-0 left-0 w-0 h-0 pointer-events-none z-[99999] mix-blend-difference transform -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{ willChange: 'transform' }}
    >
      {/* 
        CURSOR CUSTOMIZATION GUIDE:
        
        1. CENTER DOT - Change size and color:
           - Size: Change 'w-1 h-1' to 'w-2 h-2' for larger dot
           - Color: Change 'bg-white' to 'bg-sky-400', 'bg-red-500', etc.
           - Shape: Change 'rounded-full' to 'rounded-none' for square
      */}
      <div
        ref={dotRef}
        className={`absolute left-1/2 top-1/2 w-1 h-1 rounded-full transform -translate-x-1/2 -translate-y-1/2 ${
          color === 'red' ? 'bg-red-500' : color === 'green' ? 'bg-emerald-500' : color === 'purple' ? 'bg-purple-500' : 'bg-white'
        }`}
        style={{ willChange: 'transform' }}
      />
      
      {/* 
        2. CORNER BRACKETS - Customize appearance:
           - Size: Change 'w-2 h-2' to 'w-3 h-3' for larger brackets
           - Border Width: Change 'border-[2px]' to 'border-[1px]' or 'border-[3px]'
           - Color: Change 'border-white' to 'border-sky-400', 'border-red-500', etc.
           - Position: Controlled by GSAP above (lines ~80-84 and ~269-273)
      */}
      {/* Top-left */}
      <div
        className={`target-cursor-corner absolute left-1/2 top-1/2 w-2 h-2 border-[2px] rounded-tl-[6px] transform -translate-x-1/2 -translate-y-1/2 border-r-0 border-b-0 ${
          color === 'red' ? 'border-red-500' : color === 'green' ? 'border-emerald-500' : color === 'purple' ? 'border-purple-500' : 'border-blue-400'
        }`}
        style={{ willChange: 'transform' }}
      />
      {/* Top-right */}
      <div
        className={`target-cursor-corner absolute left-1/2 top-1/2 w-2 h-2 border-[2px] rounded-tr-[6px] transform -translate-x-1/2 -translate-y-1/2 border-l-0 border-b-0 ${
          color === 'red' ? 'border-red-500' : color === 'green' ? 'border-emerald-500' : color === 'purple' ? 'border-purple-500' : 'border-blue-400'
        }`}
        style={{ willChange: 'transform' }}
      />
      {/* Bottom-right */}
      <div
        className={`target-cursor-corner absolute left-1/2 top-1/2 w-2 h-2 border-[2px] rounded-br-[6px] transform -translate-x-1/2 -translate-y-1/2 border-l-0 border-t-0 ${
          color === 'red' ? 'border-red-500' : color === 'green' ? 'border-emerald-500' : color === 'purple' ? 'border-purple-500' : 'border-blue-400'
        }`}
        style={{ willChange: 'transform' }}
      />
      {/* Bottom-left */}
      <div
        className={`target-cursor-corner absolute left-1/2 top-1/2 w-2 h-2 border-[2px] rounded-bl-[6px] transform -translate-x-1/2 -translate-y-1/2 border-r-0 border-t-0 ${
          color === 'red' ? 'border-red-500' : color === 'green' ? 'border-emerald-500' : color === 'purple' ? 'border-purple-500' : 'border-blue-400'
        }`}
        style={{ willChange: 'transform' }}
      />
      
      {/*
        3. ADDITIONAL CUSTOMIZATION OPTIONS:
           - Add glow effect: Add 'drop-shadow-[0_0_10px_rgba(56,189,248,0.5)]' to className
           - Change blend mode: Change 'mix-blend-difference' to 'mix-blend-normal' or 'mix-blend-screen'
           - Add background circle: Add a div with 'bg-white/10 rounded-full' behind the dot
      */}
    </div>
  );
};

export default TargetCursor;
